import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/api';
import { CartaEntryOut, EventOut, EventVenueOut, ProductOut } from '@/types/api';
import Button from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import { usePriceFormat } from '@/hooks/usePriceFormat';
import { countryTaxRateFor } from '@/utils/countries';

interface ProductCartaPanelProps {
  product: ProductOut;
  /** Events of the product's Local (the only ones it can be listed in). */
  events: EventOut[];
  /** Country tax rates of the product's kind (drink or food rates). */
  countryTaxRates: Record<string, number>;
  /** Organizers/super admins can add listings; warehouse staff only see them. */
  canAddToCarta: boolean;
}

/**
 * Where a catalog product is sold ("En cartas": event, bar, sale price) and,
 * for organizers, a short form to add it to the carta of another bar.
 */
const ProductCartaPanel: React.FC<ProductCartaPanelProps> = ({ product, events, countryTaxRates, canAddToCarta }) => {
  const { t } = useTranslation();
  const { format } = usePriceFormat();
  const [entries, setEntries] = useState<CartaEntryOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [eventId, setEventId] = useState<number | ''>('');
  const [venues, setVenues] = useState<EventVenueOut[]>([]);
  const [venueId, setVenueId] = useState<number | ''>('');
  const [price, setPrice] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [taxIncluded, setTaxIncluded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await apiService.getProductCarta(product.id));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [product.id, t]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleEventChange = async (value: string) => {
    const id = value ? parseInt(value) : '';
    setEventId(id);
    setVenueId('');
    setVenues([]);
    if (!id) return;
    const event = events.find((e) => e.id === id);
    const countryRate = countryTaxRateFor(countryTaxRates, event?.country);
    setTaxRate(countryRate ? String(countryRate) : '');
    try {
      setVenues(await apiService.getEventVenues(id));
    } catch {
      setVenues([]);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const entered = parseFloat(price.replace(',', '.'));
    if (!eventId || isNaN(entered) || entered <= 0) {
      setError(t('products.carta.priceRequired'));
      return;
    }
    const rate = parseFloat(taxRate.replace(',', '.')) || 0;
    const basePrice = taxIncluded && rate > 0 ? Number((entered / (1 + rate / 100)).toFixed(2)) : entered;

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await apiService.addProductToCarta(product.id, {
        event_id: eventId,
        event_venue_id: venueId || null,
        price: basePrice,
        tax_rate: rate,
      });
      setSuccess(t('products.carta.added'));
      setPrice('');
      setVenueId('');
      await loadEntries();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="products-page__carta">
      <h5 className="products-page__carta-title">{t('products.carta.inCartas')}</h5>
      {loading ? (
        <p className="products-page__carta-empty">{t('common.loading')}</p>
      ) : entries.length === 0 ? (
        <p className="products-page__carta-empty">{t('products.carta.notInCarta')}</p>
      ) : (
        <ul className="products-page__carta-list">
          {entries.map((entry) => (
            <li key={`${entry.drink_id ?? ''}-${entry.food_id ?? ''}`}>
              <span>{entry.event_name}</span>
              <span>{entry.venue_name || t('management.drinks.pista', { defaultValue: 'Pista' })}</span>
              <strong>{format(entry.price)}</strong>
            </li>
          ))}
        </ul>
      )}

      {canAddToCarta && (
        events.length === 0 ? (
          <p className="products-page__carta-empty">{t('products.carta.noEvents')}</p>
        ) : (
          <form className="products-page__carta-form" onSubmit={handleAdd}>
            <h5 className="products-page__carta-title">{t('products.carta.addToCarta')}</h5>
            <div className="products-page__carta-form-grid">
              <SearchableSelect<number>
                name="carta_event"
                label={t('products.carta.event')}
                placeholder={t('products.carta.selectEvent')}
                options={events.map((event) => ({ value: event.id, label: event.name }))}
                value={eventId === '' ? null : eventId}
                onChange={(value) => handleEventChange(value === null ? '' : String(value))}
                disabled={submitting}
                required
              />
              <SearchableSelect<number>
                name="carta_bar"
                label={t('products.carta.bar')}
                placeholder={t('products.carta.pista')}
                clearable
                options={venues.map((venue) => ({ value: venue.id, label: venue.name }))}
                value={venueId === '' ? null : venueId}
                onChange={(value) => setVenueId(value ?? '')}
                disabled={submitting || !eventId}
              />
              <FormField
                type="number"
                name="carta_price"
                label={t('products.carta.salePrice')}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={submitting}
                min="0"
                step="0.01"
                required
              />
              <FormField
                type="number"
                name="carta_tax"
                label={t('products.carta.taxRate')}
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                disabled={submitting}
                min="0"
                max="99"
                step="0.1"
                helperText={t('products.carta.taxRateHelper')}
              />
            </div>
            <label className="products-page__carta-checkbox">
              <input
                type="checkbox"
                checked={taxIncluded}
                onChange={(e) => setTaxIncluded(e.target.checked)}
                disabled={submitting}
              />
              <span>{t('products.carta.taxIncluded')}</span>
            </label>
            {error && <p className="products-page__carta-error">{error}</p>}
            {success && <p className="products-page__carta-success">{success}</p>}
            <div className="products-page__form-actions">
              <Button type="submit" variant="primary" size="sm" disabled={submitting || !eventId} loading={submitting}>
                {t('products.carta.add')}
              </Button>
            </div>
          </form>
        )
      )}
      {!canAddToCarta && error && <p className="products-page__carta-error">{error}</p>}
    </div>
  );
};

export default ProductCartaPanel;
